import { Pause, Play } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

interface SecurePlayerProps {
  src: string;
  title: string;
  watermarkText: string;
  onTimeUpdate?: (currentTime: number) => void;
  videoRef?: React.RefObject<HTMLVideoElement | null>;
}

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function SecurePlayer({ src, title, watermarkText, onTimeUpdate, videoRef: externalRef }: SecurePlayerProps) {
  const internalRef = useRef<HTMLVideoElement>(null);
  const videoRef = externalRef ?? internalRef;
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [watermarkPos, setWatermarkPos] = useState({ top: 10, left: 10 });

  // Move watermark periodically to prevent screen-capture removal
  useEffect(() => {
    const interval = setInterval(() => {
      setWatermarkPos({
        top: 8 + Math.random() * 20,
        left: 5 + Math.random() * 50,
      });
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setCurrentTime(video.currentTime);
    onTimeUpdate?.(video.currentTime);
  }, [onTimeUpdate, videoRef]);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (Number.isFinite(video.duration)) {
      setDuration(video.duration);
    }
  }, [videoRef]);

  const handleDurationChange = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (Number.isFinite(video.duration)) {
      setDuration(video.duration);
    }
  }, [videoRef]);

  const handlePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }, [videoRef]);

  const handleSeek = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const video = videoRef.current;
      if (!video) return;
      const time = Number(e.target.value);
      video.currentTime = time;
      setCurrentTime(time);
    },
    [videoRef],
  );

  const handleSpeedChange = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const currentIdx = SPEED_OPTIONS.indexOf(speed as (typeof SPEED_OPTIONS)[number]);
    const nextIdx = (currentIdx + 1) % SPEED_OPTIONS.length;
    const nextSpeed = SPEED_OPTIONS[nextIdx] ?? 1;
    video.playbackRate = nextSpeed;
    setSpeed(nextSpeed);
  }, [speed, videoRef]);

  const handlePlay = useCallback(() => setIsPlaying(true), []);
  const handlePause = useCallback(() => setIsPlaying(false), []);
  const handleEnded = useCallback(() => setIsPlaying(false), []);

  // Prevent right-click context menu on video
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const now = new Date().toLocaleString();

  return (
    <div className='relative overflow-hidden rounded-lg bg-black'>
      {/* Video element — no native controls, download disabled */}
      <video
        ref={videoRef}
        src={src}
        preload='metadata'
        controlsList='nodownload noremoteplayback'
        disablePictureInPicture
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onDurationChange={handleDurationChange}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
        onContextMenu={handleContextMenu}
        className='w-full cursor-pointer'
        onClick={handlePlayPause}
      />

      {/* Watermark overlay */}
      <div
        className='pointer-events-none absolute select-none transition-all duration-1000 ease-in-out'
        style={{ top: `${watermarkPos.top}%`, left: `${watermarkPos.left}%` }}>
        <div className='text-white/30 text-xs font-mono leading-tight'>
          <div>{watermarkText}</div>
          <div>{now}</div>
        </div>
      </div>

      {/* Bottom watermark — static */}
      <div className='pointer-events-none absolute bottom-10 right-3 select-none'>
        <div className='text-white/20 text-[10px] font-mono'>{watermarkText}</div>
      </div>

      {/* Custom controls */}
      <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 pb-2 pt-6'>
        {/* Progress bar */}
        <input
          type='range'
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={handleSeek}
          className='mb-2 h-1 w-full cursor-pointer appearance-none rounded-full bg-white/30 accent-blue-500 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500'
        />

        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            {/* Play/Pause */}
            <button
              type='button'
              onClick={handlePlayPause}
              className='text-white hover:text-blue-400 transition-colors'>
              {isPlaying ? <Pause className='h-4 w-4' /> : <Play className='h-4 w-4' />}
            </button>

            {/* Time display */}
            <span className='text-xs text-white/80 font-mono tabular-nums'>
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <div className='flex items-center gap-3'>
            {/* Title */}
            <span className='text-xs text-white/60 truncate max-w-[120px]'>{title}</span>

            {/* Speed control */}
            <button
              type='button'
              onClick={handleSpeedChange}
              className={cn(
                'rounded px-1.5 py-0.5 text-xs font-medium transition-colors',
                speed === 1 ? 'text-white/60 hover:text-white' : 'bg-blue-600/80 text-white',
              )}>
              {speed}x
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
